import React from 'react'
import ProgressBar from './ProgressBar.js'
import FormPage1 from './Pages/FormPage1.js'
import FormPage2 from './Pages/FormPage2.js'
import FormPage3 from './Pages/FormPage3.js'
import Button from '../../../ui/Button.js'
import { QUESTIONS } from '../questions.js'
import usePhoneLayout from '../../../../hooks/usePhoneLayout.js'



// Fresh form state. Questions carry the scoring metadata the backend stores, without the statement text.
function initialFormData() {
    return {
        nickname: '',
        email: '',
        country: '',
        consentCheck: false,
        error: {
            nickname: false,
            email: false,
            country: false,
            consentCheck: false,
            sexData: false,
            ageGroupData: false,
            questions: QUESTIONS.map(() => false) // one per question
        },
        questions: QUESTIONS.map(({ num, optionAChar, optionBChar, valueIfA, valueIfB }) => (
            { num, optionAChar, optionBChar, valueIfA, valueIfB, answer: "" }
        ))
    }
}

// Progress saved by updateLocalStorage(); unreadable data starts a fresh form
function readSavedProgress() {
    try {
        const saved = localStorage.getItem('kizaboFormData');
        return saved ? JSON.parse(saved) : null;
    } catch (error) {
        return null;
    }
}

// How long a chosen answer stays visible before the next question appears
const AUTO_ADVANCE_MS = 350;

// Where Next leads: the next question on phones, otherwise the next page
function nextPosition(page, step, phone) {
    return (phone && page > 1 && page < 12 && step < 2) ? [page, step + 1] : [page + 1, 0];
}

export default function AppForm(props) {

    //Load information from localStorage once, when the form mounts (IMP-052)
    const [savedProgress] = React.useState(readSavedProgress);

    //Start of the form
    const [pageNum, setPageNum] = React.useState(savedProgress?.pageNum ?? 1)
    // Phones only: which of the page's three questions is shown (0-2); ignored on wider screens
    const [pageStep, setPageStep] = React.useState(savedProgress?.pageStep ?? 0);
    const isPhone = usePhoneLayout();
    // Direction of the last move, for the slide-in transition (IMP-092); none on first load
    const [direction, setDirection] = React.useState("none");
    const [submitting, setSubmitting] = React.useState(false);
    const oneAtATime = isPhone && pageNum > 1 && pageNum < 12;
    const step = oneAtATime ? pageStep : 0;

    const [userId, setUserId] = React.useState(null);
    // Scores and report text returned by the backend after submitting, shown on the results page (IMP-085)
    const [results, setResults] = React.useState(null);
    // Older backends don't report emailSent; treat a missing value as sent
    const [emailSent, setEmailSent] = React.useState(true);

    const [formData, setFormData] = React.useState(() => savedProgress?.formData ?? initialFormData());
    const [selectSexData, setSelectSexData] = React.useState(savedProgress?.selectSexData ?? '');

    const handleSelectSexChange = (selectedOption) => {
        setSelectSexData(selectedOption);
        // Reset error for the field being changed
        console.log(selectedOption)
        setFormData((oldState) => {
            return {
                ...oldState,
                error: {
                    ...oldState.error,
                    sexData: false,
                }
            }
        })
    }


    const [selectAgeGroupData, setSelectAgeGroupData] = React.useState(savedProgress?.selectAgeGroupData ?? '');

    const handleSelectAgeGroupChange = (selectedOption) => {
        setSelectAgeGroupData(selectedOption);
        // Reset error for the field being changed
        setFormData((oldState) => {
            return {
                ...oldState,
                error: {
                    ...oldState.error,
                    ageGroupData: false,
                }
            }
        })
    }


    function handleChange(event) {
        const { type, name, value, checked } = event.target;
        const num = parseInt(name.replace("question", ""), 10)
        if (type === "radio") {
            setFormData((oldState) => {
                return {
                    ...oldState,
                    error: {
                        ...oldState.error,
                        questions: oldState.error.questions.map((currentArrayValue, index) => {
                            if (index === num - 1) {
                                return false; // Reset the error for the answered question
                            } else {
                                return currentArrayValue; // Keep the other errors unchanged
                            }
                        })
                    },
                    questions: oldState.questions.map((perQuestion) => {
                        if (perQuestion.num === name) {
                            return {
                                ...perQuestion,
                                answer: value
                            }
                        } else {
                            return perQuestion;
                        }
                    })
                }
            })
            if (oneAtATime) {
                // A light tap on Android phones confirms the choice; other browsers ignore it (IMP-093)
                if (navigator.vibrate) navigator.vibrate(10);
                scheduleAutoAdvance(num - 1);
            }

        } else {
            setFormData((oldState) => {
                return {
                    ...oldState,
                    [name]: type === "checkbox" ? checked : value,
                    error: {
                        ...oldState.error,
                        [name]: false // Reset error for the field being changed
                    }
                };
            });
        }
    }

    // Save progress on every change, so a reload never loses answers (IMP-078). Same shape as before;
    // nothing is kept once the results page (12) is reached.
    React.useEffect(() => {
        if (pageNum === 12) return;
        try {
            localStorage.setItem('kizaboFormData', JSON.stringify({
                pageNum: pageNum,
                pageStep: pageStep,
                formData: formData,
                selectSexData: selectSexData,
                selectAgeGroupData: selectAgeGroupData
            }));
        } catch (error) {
            // Storage full or blocked: the form still works, progress just isn't kept
        }
    }, [pageNum, pageStep, formData, selectSexData, selectAgeGroupData]);

    // Each page (on phones, each question) is a browser history step, so the browser/phone back gesture
    // goes to the previous one instead of leaving the assessment (IMP-079, IMP-083). The position travels
    // in history.state (kizaboPage, kizaboStep, and kizaboIndex = entry number); the URL stays the same.
    const positionRef = React.useRef(null);
    positionRef.current = { page: pageNum, step: pageStep, phone: isPhone };

    function pushPosition(page, step) {
        const index = (window.history.state?.kizaboIndex ?? 1) + 1;
        window.history.pushState({ ...window.history.state, kizaboPage: page, kizaboStep: step, kizaboIndex: index }, '');
    }

    React.useEffect(() => {
        const { page: currentPage, step: currentStep, phone } = positionRef.current;
        const entry = window.history.state;
        if (!entry?.kizaboPage) {
            // First visit to this entry: one entry per page (per question on phones) up to the current
            // position, so Back also works after resuming saved progress
            window.history.replaceState({ ...entry, kizaboPage: 1, kizaboStep: 0, kizaboIndex: 1 }, '');
            for (let page = 2; page <= currentPage; page++) {
                const lastStep = (!phone || page > 11) ? 0 : (page < currentPage ? 2 : currentStep);
                for (let step = 0; step <= lastStep; step++) pushPosition(page, step);
            }
        } else if (entry.kizaboPage !== currentPage || (entry.kizaboStep ?? 0) !== currentStep) {
            // Reload of an entry whose saved progress was cleared: trust the form state
            window.history.replaceState({ ...entry, kizaboPage: currentPage, kizaboStep: currentStep }, '');
        }

        function handlePopState(event) {
            const state = event.state;
            if (!state?.kizaboPage) return;
            if (positionRef.current.page === 12) {
                // Already submitted: leave the form instead of going back into it
                // (this entry is number kizaboIndex, so that many steps back is the page before the form)
                window.history.go(-(state.kizaboIndex ?? state.kizaboPage));
                return;
            }
            const { page, step } = positionRef.current;
            const goingBack = state.kizaboPage < page || (state.kizaboPage === page && (state.kizaboStep ?? 0) < step);
            setDirection(goingBack ? "back" : "forward");
            setPageNum(state.kizaboPage);
            setPageStep(state.kizaboStep ?? 0);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    function goForward([page, step]) {
        setDirection("forward");
        pushPosition(page, step);
        setPageNum(page);
        setPageStep(step);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Phones: after an answer, show the choice briefly, then the next question (IMP-083). Repeated taps
    // move on only once; the last question waits for Submit.
    const autoAdvanceTimer = React.useRef(null);
    React.useEffect(() => () => clearTimeout(autoAdvanceTimer.current), []);

    function scheduleAutoAdvance(questionIndex) {
        clearTimeout(autoAdvanceTimer.current);
        if (questionIndex >= QUESTIONS.length - 1) return;
        autoAdvanceTimer.current = setTimeout(() => {
            const { page, step, phone } = positionRef.current;
            if (!phone || (page - 2) * 3 + step !== questionIndex) return; // already moved on
            goForward(nextPosition(page, step, phone));
        }, AUTO_ADVANCE_MS);
    }

    // Fetch the submit animation ahead of time on the last question page (IMP-088)
    React.useEffect(() => {
        if (pageNum === 11) import('./LoadingAnimation.js');
    }, [pageNum]);

    // On phones the page content scrolls inside its own area (IMP-087); start each page at its top, and
    // move focus to the new question so screen readers announce it (IMP-083)
    const formRef = React.useRef(null);
    React.useEffect(() => {
        const content = formRef.current && formRef.current.querySelector('.Form');
        if (content) content.scrollTop = 0;
        const question = positionRef.current.phone && content && content.querySelector('.RadioInput');
        if (question) question.focus({ preventScroll: true });
    }, [pageNum, pageStep]);

    // Scroll to and focus the first field or question that needs attention (IMP-081)
    function focusProblem(elementId) {
        const element = document.getElementById(elementId);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });
    }

    function checkPageOneErrors(formData, sexData, ageGroupData) {
        //Cycle through all of the fields to check if they're empty or false or not
        const fieldsToCheck = ["nickname", "email", "country", "consentCheck", "sexData", "ageGroupData"];
        const newErrors = {};

        fieldsToCheck.forEach((field) => {
            const value = (field === "sexData") ?
                sexData : (field === "ageGroupData") ?
                    ageGroupData : formData[field];
            if (field === "email") {
                //Check if the email is valid
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                newErrors[field] = !emailRegex.test(value);
            } else {
                // Empty or whitespace-only text, or an unticked checkbox
                newErrors[field] = value === false || (typeof value === 'string' && value.trim() === '');
            }

        });

        //Update the formData and the Select Data
        setFormData((oldState) => {
            return {
                ...oldState,
                error: {
                    ...oldState.error,
                    ...newErrors
                }
            }
        });
        console.log("Checking errors for page 1:", newErrors);
        // Fields in the order they appear on the page
        const fieldIds = { nickname: "field-nickname", email: "field-email", country: "field-country", sexData: "field-sex", ageGroupData: "field-agegroup", consentCheck: "field-consentCheck" };
        const firstProblem = Object.keys(fieldIds).find((field) => newErrors[field]);
        if (firstProblem) focusProblem(fieldIds[firstProblem]);
        return (newErrors.nickname || newErrors.email || newErrors.country || newErrors.consentCheck || newErrors.sexData || newErrors.ageGroupData);
    }

    // first/count: the questions to check (the whole page, or on phones the question on screen)
    function checkBinaryQuestionErrors(formData, first, count) {
        //Get the current page's questions
        const currentPageQuestions = formData.questions.slice(first, first + count);

        //Check if any of the questions are unanswered
        const unansweredQuestions = currentPageQuestions.filter((question) => question.answer === "");

        //Find the indexes of the unanswered questions. Outputs ["question1", "question2", "question3"]
        const unansweredObjectIndexes = unansweredQuestions.map((question) => question.num);


        //Parsing the questionNum (ex: "question1") to just the number (ex: 1)
        /*const unansweredIndexes = unansweredObjectIndexes.map((questionNum) => parseInt(questionNum.replace("question", ""), 10))
        console.log("Unanswered questions:", unansweredIndexes);*/

        unansweredObjectIndexes.forEach((questionNum) => {
            const num = parseInt(questionNum.replace("question", ""), 10)

            setFormData((oldState) => {
                return {
                    ...oldState,
                    error: {
                        ...oldState.error,
                        questions: oldState.error.questions.map((currentArrayValue, index) => {
                            if (index === num - 1) {
                                return true; // Set the error for the unanswered question to true
                            } else {
                                return currentArrayValue; // Keep the other errors unchanged
                            }
                        })
                    }
                }
            })
        })

        if (unansweredObjectIndexes.length > 0) {
            const firstUnanswered = parseInt(unansweredObjectIndexes[0].replace("question", ""), 10) - 1;
            if (oneAtATime && firstUnanswered !== startIndex + step) {
                setPageStep(firstUnanswered - startIndex); // phones: show the question that is missing
            } else {
                focusProblem(`${unansweredObjectIndexes[0]}-optiona`);
            }
        }

        //If there are unanswered questions, we want to return true
        return unansweredObjectIndexes.length > 0;
    }

    function checkErrors() {
        //If page 1, we want to check first if everything was filled out correctly
        if (pageNum === 1) {
            //If there are errors, we do not want to increment the pageNum

            // initially, selectSexData.value is undefined, so we need to check if it exists
            const workaroundSexData = (selectSexData === '') ? selectSexData : selectSexData.value
            const workaroundAgeGroupData = (selectAgeGroupData === '') ? selectAgeGroupData : selectAgeGroupData.value
            if (checkPageOneErrors(formData, workaroundSexData, workaroundAgeGroupData)) {
                console.log("Errors found, not incrementing pageNum");
                return true;
            } else {
                console.log("No errors found, incrementing pageNum");
                return false;
            }
        } else if (pageNum > 1 && pageNum < 12) {
            //If page 2 or higher, we want to check the binary questions if they were answered
            // Phones check the question on screen, and the whole page before leaving it
            const [first, count] = (oneAtATime && step < 2) ? [startIndex + step, 1] : [startIndex, 3];
            if (checkBinaryQuestionErrors(formData, first, count)) {
                console.log("Errors found, not incrementing pageNum");
                return true;
            } else {
                console.log("No errors found, incrementing pageNum");
                return false;
            }
        }
    }

    function handleNext() {
        console.log("Next button clicked");
        // With errors, the first problem has already been scrolled into view
        if (!checkErrors()) {
            goForward(nextPosition(pageNum, step, isPhone));
        }
    }

    // Same as the browser's back button, so the on-screen Back and the history stay in step
    function handleBack() {
        console.log("Back button clicked");
        const entry = window.history.state;
        if (entry?.kizaboPage === pageNum && (entry.kizaboStep ?? 0) === pageStep) {
            window.history.back();
        } else if (oneAtATime && step > 0) {
            setDirection("back");
            setPageStep(step - 1);
        } else {
            setDirection("back");
            // On phones the previous page is entered at its last question
            setPageNum(pageNum - 1);
            setPageStep(isPhone && pageNum > 2 ? 2 : 0);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }



    function handleSubmit(event) {
        event.preventDefault();

        // Only the last question page (on phones, its last question) submits; ignore implicit (Enter key)
        // submission elsewhere
        if (pageNum !== 11 || (oneAtATime && step !== 2)) return;

        //If there are no errors, then submit the form
        if (submitting) return; // one submission at a time
        if (!checkErrors()) {
            setSubmitting(true);
            props.handleTogglePopup({ show: true, type: "loading", });
            //if successful, increment pageNum to 12
            console.log('Form submitted');
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/submit/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_KIZABO_API_KEY },
                body: JSON.stringify({
                    ...formData,
                    sex: selectSexData.value,
                    age_group: selectAgeGroupData.value
                })
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`Submit failed with status ${response.status}`);
                }
                return response.json(); // parse JSON body
            }).then(data => {
                if (data.message !== "Success") {
                    throw new Error('Submit returned an unexpected response');
                }
                console.log("Form submitted successfully", data);
                setUserId(data.user_id);
                setEmailSent(data.emailSent !== false);
                if (Array.isArray(data.scores) && data.scores.length === 5) {
                    const myResults = { scores: data.scores, descriptions: Array.isArray(data.descriptions) ? data.descriptions : [] };
                    setResults(myResults);
                    try {
                        // Kept on this device so matching can use your own scores (demo mode, IMP-070)
                        localStorage.setItem('kizaboMyResults', JSON.stringify(myResults));
                    } catch (error) {
                        // the page still shows them
                    }
                }
                localStorage.removeItem("kizaboFormData")
                window.history.replaceState({ ...window.history.state, kizaboPage: 12 }, '');
                setSubmitting(false);
                setDirection("results");
                setPageNum(12);
                props.handleTogglePopup({ show: false, type: "loading", });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }).catch(error => {
                // Network errors, error statuses and unexpected responses all show the error popup;
                // the answers stay in place so the user can submit again
                console.error("Form submission failed:", error);
                setSubmitting(false);
                props.handleTogglePopup({ show: true, type: "error", });
            });
        }
    }

    //Limits the pages being shown to just 3 questions at a time
    const startIndex = ((pageNum - 1) * 3) - 3;
    const showSubmit = pageNum === 11 && (!oneAtATime || step === 2);


    return (
        <div className={`AppForm AppForm--enter-${direction}`}>
            <div className="AppForm--header">
                <div className="AppForm--logo-space"></div>
                <ProgressBar
                    pageNum={pageNum}
                    questionNumber={oneAtATime ? startIndex + step + 1 : null}
                />
            </div>
            <form className="AppForm--inputs-container" onSubmit={handleSubmit} noValidate ref={formRef}>
                {(pageNum === 1) && <FormPage1
                    style={{ display: pageNum === 1 ? 'block' : 'none' }}
                    handleNext={handleNext}
                    handleBack={handleBack}
                    formData={formData}
                    handleChange={handleChange}
                    selectSexData={selectSexData}
                    handleSelectSexChange={handleSelectSexChange}
                    selectAgeGroupData={selectAgeGroupData}
                    handleSelectAgeGroupChange={handleSelectAgeGroupChange}
                    pageNum={pageNum}
                />
                }
                {((pageNum > 1) && (pageNum < 12)) && <FormPage2
                    key={`${pageNum}-${step}` /* a new element per page/question, so its entrance animates */}
                    handleNext={handleNext}
                    handleBack={handleBack}
                    formData={formData}
                    handleChange={handleChange}
                    pageNum={pageNum}
                    firstQuestion={startIndex + step}
                    questionCount={oneAtATime ? 1 : 3}
                />}
                {(pageNum === 12) && <FormPage3
                    email={formData.email}
                    userId={userId}
                    emailSent={emailSent}
                    results={results}
                />}
                <div className="Form--submit-container">
                    {/*First and last pages should not have a back button*/}
                    {((pageNum !== 1) && (pageNum !== 12)) && <Button variant="secondary" className="Form--nav-button" onClick={handleBack}>Back</Button>}

                    {/*If second to the last page, show SUBMIT instead of NEXT */}
                    {(pageNum < 12 && !showSubmit) && <Button variant="inverse" className="Form--nav-button" onClick={handleNext}>Next</Button>}
                    {showSubmit && <Button variant="inverse" className="Form--nav-button" type="submit" loading={submitting}>Submit</Button>}
                </div>
            </form>
            <div className="AppForm--bottom-space"></div>

        </div>
    )
}